import React, { useState, useEffect } from "react";

const PFCalculator = () => {
  const [basicSalary, setBasicSalary] = useState(50000);
  const [employeeContribution, setEmployeeContribution] = useState(12);
  const [yearsOfService, setYearsOfService] = useState(10);
  const [currentAge, setCurrentAge] = useState(30);
  const [expectedInterest, setExpectedInterest] = useState(8.25);
  const [result, setResult] = useState({
    monthlyBasic: "0.00",
    employeeMonthlyPF: "0.00",
    employerMonthlyPF: "0.00",
    totalMonthlyPF: "0.00",
    employeeContributionTotal: "0.00",
    employerContributionTotal: "0.00",
    interestEarned: "0.00",
    totalPFBalance: "0.00",
    retirementAge: "0",
    yearsToRetirement: "0",
    projectedRetirementCorpus: "0.00",
    savingTip: "",
  });

  // PF Calculation Function
  const calculatePF = () => {
    let basic = parseFloat(basicSalary);
    let empContribution = parseFloat(employeeContribution);
    let years = parseFloat(yearsOfService);
    let age = parseFloat(currentAge);
    let interest = parseFloat(expectedInterest);

    if (isNaN(basic) || basic < 0 || isNaN(empContribution) || empContribution < 0 || isNaN(years) || years < 0 || isNaN(age) || age < 0 || isNaN(interest) || interest < 0) return;

    // Cap basic salary at ₹15,000 for PF calculation (statutory limit)
    const pfBasic = Math.min(basic, 15000);
    
    // Employee contribution (12% of basic or custom %)
    const employeeMonthlyPF = (pfBasic * empContribution) / 100;
    
    // Employer contribution breakdown:
    // 3.67% to EPF, 8.33% to EPS (max ₹1,250), remaining to EPF
    const epsContribution = Math.min((pfBasic * 8.33) / 100, 1250);
    const epfEmployerContribution = (pfBasic * 3.67) / 100;
    const employerMonthlyPF = epfEmployerContribution + epsContribution;
    
    const totalMonthlyPF = employeeMonthlyPF + employerMonthlyPF;
    
    // Total contributions over years
    const totalMonths = years * 12;
    const employeeContributionTotal = employeeMonthlyPF * totalMonths;
    const employerContributionTotal = employerMonthlyPF * totalMonths;
    
    // Calculate interest earned (compound interest)
    const monthlyInterest = interest / 12 / 100;
    let balance = 0;
    for (let i = 0; i < totalMonths; i++) {
      balance = (balance + totalMonthlyPF) * (1 + monthlyInterest);
    }
    
    const totalPFBalance = balance;
    const interestEarned = totalPFBalance - employeeContributionTotal - employerContributionTotal;
    
    // Retirement projection (assuming retirement at 58)
    const retirementAge = 58;
    const yearsToRetirement = Math.max(0, retirementAge - age);
    const monthsToRetirement = yearsToRetirement * 12;
    
    let projectedBalance = totalPFBalance;
    for (let i = 0; i < monthsToRetirement; i++) {
      projectedBalance = (projectedBalance + totalMonthlyPF) * (1 + monthlyInterest);
    }
    const projectedRetirementCorpus = projectedBalance;

    let savingTip = "";
    if (totalPFBalance < 500000) {
      savingTip = "Consider increasing your PF contribution through VPF for better retirement corpus.";
    } else if (totalPFBalance < 1000000) {
      savingTip = "Good PF accumulation. Consider VPF to boost your retirement savings.";
    } else if (totalPFBalance < 2000000) {
      savingTip = "Excellent PF corpus! You're on track for a comfortable retirement.";
    } else if (totalPFBalance < 5000000) {
      savingTip = "Outstanding PF balance! You're building a strong retirement fund.";
    } else {
      savingTip = "Exceptional PF corpus! Your retirement is well-secured.";
    }

    setResult({
      monthlyBasic: pfBasic.toFixed(2),
      employeeMonthlyPF: employeeMonthlyPF.toFixed(2),
      employerMonthlyPF: employerMonthlyPF.toFixed(2),
      totalMonthlyPF: totalMonthlyPF.toFixed(2),
      employeeContributionTotal: employeeContributionTotal.toFixed(2),
      employerContributionTotal: employerContributionTotal.toFixed(2),
      interestEarned: interestEarned.toFixed(2),
      totalPFBalance: totalPFBalance.toFixed(2),
      retirementAge: retirementAge.toString(),
      yearsToRetirement: yearsToRetirement.toFixed(0),
      projectedRetirementCorpus: projectedRetirementCorpus.toFixed(2),
      savingTip,
    });
  };

  useEffect(() => {
  calculatePF();
}, [
  basicSalary,
  employeeContribution,
  yearsOfService,
  currentAge,
  expectedInterest
]);


  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
          </div>
          <h1>Public Fund Calculator </h1>
          <p>
            Calculate Provident Fund (EPF) contributions, interest earned, and retirement corpus projections
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Employment & Contribution Details
              </h2>

              {/* Basic Salary */}
              <div className="input-group">
                <label>Basic Salary (Monthly)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={basicSalary}
                    onChange={(e) => setBasicSalary(e.target.value)}
                    className="calc-input"
                    placeholder="50000"
                  />
                </div>
                <div className="quick-select-group">
                  {[15000, 30000, 50000, 75000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setBasicSalary(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 1000 ? `${(amount / 1000).toFixed(0)}K` : amount}
                    </button>
                  ))}
                </div>
              </div>

              {/* Employee Contribution % */}
              <div className="input-group">
                <label>Employee Contribution (%)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">%</span>
                  <input
                    type="number"
                    value={employeeContribution}
                    onChange={(e) => setEmployeeContribution(e.target.value)}
                    className="calc-input"
                    placeholder="12"
                  />
                </div>
                <div className="quick-select-group">
                  {[12, 15, 20, 25].map((percent) => (
                    <button
                      key={percent}
                      onClick={() => setEmployeeContribution(percent)}
                      className="quick-select-btn success"
                    >
                      {percent}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Years of Service */}
              <div className="input-group">
                <label>Years of Service Completed</label>
                <div className="input-wrapper">
                  <span className="input-prefix">Yrs</span>
                  <input
                    type="number"
                    value={yearsOfService}
                    onChange={(e) => setYearsOfService(e.target.value)}
                    className="calc-input"
                    placeholder="10"
                  />
                </div>
                <div className="quick-select-group">
                  {[5, 10, 15, 20].map((years) => (
                    <button
                      key={years}
                      onClick={() => setYearsOfService(years)}
                      className="quick-select-btn secondary"
                    >
                      {years} Years
                    </button>
                  ))}
                </div>
              </div>

              {/* Current Age */}
              <div className="input-group">
                <label>Current Age</label>
                <div className="input-wrapper">
                  <span className="input-prefix">Age</span>
                  <input
                    type="number"
                    value={currentAge}
                    onChange={(e) => setCurrentAge(e.target.value)}
                    className="calc-input"
                    placeholder="30"
                  />
                </div>
                <div className="quick-select-group">
                  {[25, 30, 35, 40].map((age) => (
                    <button
                      key={age}
                      onClick={() => setCurrentAge(age)}
                      className="quick-select-btn primary"
                    >
                      {age} Years
                    </button>
                  ))}
                </div>
              </div>

              {/* Expected Interest Rate */}
              <div className="input-group">
                <label>Expected Interest Rate (% p.a.)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">%</span>
                  <input
                    type="number"
                    value={expectedInterest}
                    onChange={(e) => setExpectedInterest(e.target.value)}
                    className="calc-input"
                    placeholder="8.25"
                    step="0.25"
                  />
                </div>
                <div className="quick-select-group">
                  {[8.15, 8.25, 8.5, 9.0].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setExpectedInterest(rate)}
                      className="quick-select-btn success"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculatePF} className="calculate-btn">
                Calculate PF
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                PF Balance & Projections
              </h2>

              <div className="result-card">
                {/* Total PF Balance - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Current PF Balance</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalPFBalance).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    After {yearsOfService} years of service
                  </div>
                </div>

                {/* Projected Retirement Corpus */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Projected at Retirement (58)</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.projectedRetirementCorpus).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    In {result.yearsToRetirement} years
                  </div>
                </div>

                {/* Interest Earned */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Interest Earned</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.interestEarned).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    At {expectedInterest}% per annum
                  </div>
                </div>

                {/* Monthly PF Contribution */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Monthly Contribution</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalMonthlyPF).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Employee + Employer contribution
                  </div>
                </div>

                {/* Employee Monthly */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Your Monthly Contribution</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.employeeMonthlyPF).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {employeeContribution}% of basic salary
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">PF Contribution Breakdown</h3>
                  
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Employee Contribution</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.employeeContributionTotal).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(parseFloat(result.employeeContributionTotal) / parseFloat(result.totalPFBalance)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Employer Contribution</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.employerContributionTotal).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(parseFloat(result.employerContributionTotal) / parseFloat(result.totalPFBalance)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Interest Earned</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.interestEarned).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill purple"
                        style={{
                          width: `${(parseFloat(result.interestEarned) / parseFloat(result.totalPFBalance)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Monthly Contribution Split */}
                <div className="breakdown-section" style={{marginTop: '1.5rem'}}>
                  <h3 className="breakdown-title">Monthly Contribution Split</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Employee ({employeeContribution}%)</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.employeeMonthlyPF).toLocaleString("en-IN")} /month
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(parseFloat(result.employeeMonthlyPF) / parseFloat(result.totalMonthlyPF)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Employer (12%)</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.employerMonthlyPF).toLocaleString("en-IN")} /month
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(parseFloat(result.employerMonthlyPF) / parseFloat(result.totalMonthlyPF)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Saving Tip Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>PF Investment Tip</h3>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    {result.savingTip} PF interest is tax-free and provides guaranteed returns, making it a safe retirement investment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Provident Fund (EPF) in India</h3>
              <p>
                Employee Provident Fund (EPF) is a retirement savings scheme managed by EPFO under the Ministry of Labour. 
                It applies to organizations with 20 or more employees. Both employee and employer contribute 12% of basic 
                salary (capped at ₹15,000 for statutory PF). Employee's 12% goes entirely to EPF. Employer's 12% is split: 
                3.67% to EPF and 8.33% to EPS (Employee Pension Scheme, capped at ₹1,250). The EPF interest rate is declared 
                annually by the government (currently 8.25% for FY 2023-24). Interest is compounded annually and credited to 
                your account. PF can be withdrawn partially for specific purposes (home loan, medical emergency, education) 
                or fully after retirement or 2 months of unemployment. Withdrawals before 5 years of continuous service are 
                taxable. VPF (Voluntary Provident Fund) allows additional contributions beyond 12% for higher retirement corpus.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>PF Calculation Formula:</h4>
            <div className="formula-display">
              Monthly PF = (Basic Salary × Contribution %) | Max Basic for PF = ₹15,000
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Employee Contribution</div>
                <div className="formula-param-value">12% of basic (can be higher via VPF)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Employer Contribution</div>
                <div className="formula-param-value">3.67% to EPF + 8.33% to EPS (max ₹1,250)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Interest Rate</div>
                <div className="formula-param-value">Currently 8.25% p.a. (compounded annually)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Basic Salary Cap</div>
                <div className="formula-param-value">₹15,000 for statutory PF calculation</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is the current EPF interest rate?</h5>
              <p>
                The EPF interest rate for FY 2023-24 is 8.25% per annum, compounded annually. The rate is declared by the government each year.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I withdraw PF before retirement?</h5>
              <p>
                Yes, partial withdrawals allowed for home purchase, medical emergency, education, marriage. Full withdrawal after 2 months unemployment or retirement at 58.
              </p>
            </div>

            <div className="faq-item">
              <h5>Is PF interest taxable?</h5>
              <p>
                PF interest is tax-free if withdrawn after 5 years of continuous service. Early withdrawal (before 5 years) makes it taxable as per your income slab.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is VPF and should I opt for it?</h5>
              <p>
                VPF (Voluntary PF) allows you to contribute more than 12%. It earns the same interest as EPF and is tax-free, making it a good retirement savings option.
              </p>
            </div>

            <div className="faq-item">
              <h5>What happens to PF when I change jobs?</h5>
              <p>
                You can transfer your PF to the new employer's account or keep it in the old account. Use UAN (Universal Account Number) for seamless transfers.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is the difference between EPF and EPS?</h5>
              <p>
                EPF is your retirement corpus with interest. EPS provides monthly pension after retirement at 58. Employer's contribution is split between both.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PFCalculator;